import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  createAnonymousSession,
  identifySession,
  requestUploadUrl,
  submitMetadata,
  triggerExtraction,
  getSessionStatus,
  getAnalysis,
  getExtraction,
  submitFeedback,
  type CreateSessionRequest,
  type UploadUrlRequest
} from '../api/menuAnalysis';
import type {
  PhaseOneExtractionResponse,
  PhaseTwoAnalysisResponse,
  PhaseTwoMetadataRequest,
  UploadUrlResponse,
  SessionStatusResponse
} from '../types';
import { buildBlobUploadUrl } from '../utils/blob';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

export type AnalysisStep =
  | 'upload'
  | 'email'
  | 'extraction'
  | 'review'
  | 'meta-form'
  | 'meta-basics'
  | 'meta-when-where'
  | 'meta-tax'
  | 'meta-service'
  | 'analysis'
  | 'complete';

export const useMenuAnalysis = () => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [uploadInfo, setUploadInfo] = useState<UploadUrlResponse | null>(null);
  const [extraction, setExtraction] = useState<PhaseOneExtractionResponse | null>(null);
  const [analysis, setAnalysis] = useState<PhaseTwoAnalysisResponse | null>(null);
  const [metadata, setMetadata] = useState<Partial<PhaseTwoMetadataRequest>>({});
  const [step, setStep] = useState<AnalysisStep>('upload');
  const [error, setError] = useState<string | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const isBootHydration = useRef(false);
  const analysisFetchInFlight = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [lastIdentifyPayload, setLastIdentifyPayload] = useState<CreateSessionRequest | null>(null);
  const [lastAnalysisPayload, setLastAnalysisPayload] = useState<PhaseTwoMetadataRequest | null>(null);
  const [hasFeedback, setHasFeedback] = useState<boolean>(false);

  // Simple storage shim
  const storage = useMemo(() => (typeof window !== 'undefined' ? window.localStorage : null), []);

  const persist = useCallback(() => {
    if (!storage) return;
    try {
      const payload = {
        sessionId,
        email,
        step,
        metadata,
        extraction,
        analysis
      };
      storage.setItem('labs.menuAnalysis.state', JSON.stringify(payload));
    } catch {
      // ignore quota errors
    }
  }, [analysis, email, extraction, metadata, sessionId, step, storage]);

  // Load from URL or storage on first mount
  useEffect(() => {
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      // Accept both dashed GUID and 32-hex (dashless) formats
      const match = path.match(/\/menuanalysis\/([A-Fa-f0-9\-]{32,36})/);
      let urlSession = match?.[1] ?? null;

      // Normalize dashless GUID (N format) to dashed format
      if (urlSession) {
        const compact = urlSession.replace(/-/g, '');
        if (/^[A-Fa-f0-9]{32}$/.test(compact)) {
          urlSession = compact.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
          try {
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', `/menuanalysis/${urlSession}`);
            }
          } catch {}
        }
      }

      let restored: any = null;
      if (storage) {
        const raw = storage.getItem('labs.menuAnalysis.state');
        if (raw) restored = JSON.parse(raw);
      }

      if (urlSession) {
        setSessionId(urlSession);
        // Only restore cached state if it matches the URL session
        if (restored?.sessionId === urlSession) {
          if (restored?.email) setEmail(restored.email);
          if (restored?.metadata) setMetadata(restored.metadata);
          if (restored?.extraction) setExtraction(restored.extraction);
          if (restored?.analysis) setAnalysis(restored.analysis);
          if (restored?.step) setStep(restored.step);
        }
        isBootHydration.current = true;
        setIsHydrating(true);
      } else {
        // No URL session → do not auto-resume from cache
        // Optionally, clear stale cache to avoid confusion later
        // (keeping cache is also fine if you want a manual resume button later)
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist whenever state changes
  useEffect(() => {
    persist();
  }, [persist]);

  const mergeMetadata = useCallback((updates: Partial<PhaseTwoMetadataRequest>) => {
    setMetadata((previous) => {
      const next: Partial<PhaseTwoMetadataRequest> = { ...previous };

      // If applicability is set to NotApplicable, explicitly remove stale rates
      const shouldClearTaxRate = updates.taxIncluded === 'NotApplicable' || ('taxRate' in updates && updates.taxRate === undefined);
      const shouldClearServiceRate = updates.serviceFeeIncluded === 'NotApplicable' || ('serviceFeeRate' in updates && updates.serviceFeeRate === undefined);
      if (shouldClearTaxRate) {
        delete (next as any).taxRate;
      }
      if (shouldClearServiceRate) {
        delete (next as any).serviceFeeRate;
      }

      (Object.entries(updates) as [keyof PhaseTwoMetadataRequest, unknown][]).forEach(([key, value]) => {
        // Skip undefined/null for general keys; we handle explicit clears above for rates
        if (value === undefined || value === null) {
          return;
        }

        if (typeof value === 'string' && value.trim().length === 0) {
          return;
        }

        if (key === 'taxIncluded' || key === 'serviceFeeIncluded') {
          if (value === 'Included' || value === 'Additional' || value === 'NotApplicable') {
            next[key] = value as any;
          }
          return;
        }

        // Assign with a safe cast because `keyof` index access over Partial<...>
        // collapses to a wide union and confuses TS when narrowing per-key.
        (next as Record<keyof PhaseTwoMetadataRequest, unknown>)[key] = value as unknown;
      });

      return next;
    });
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const status = await getSessionStatus(sessionId);
      if (typeof status.hasFeedback === 'boolean') setHasFeedback(status.hasFeedback);
      if (status.email) setEmail(status.email);

      if (status.status === 'AnalysisComplete') {
        if (!analysis && !analysisFetchInFlight.current) {
          try {
            analysisFetchInFlight.current = true;
            const serverAnalysis = await getAnalysis(sessionId);
            setAnalysis(serverAnalysis);
            if (serverAnalysis.serverMetadata) {
              mergeMetadata({
                eventName: (serverAnalysis.serverMetadata as any)?.eventName,
                eventType: serverAnalysis.serverMetadata.eventType,
                eventLocation: serverAnalysis.serverMetadata.eventLocation,
                eventDate: serverAnalysis.serverMetadata.eventDate,
                taxIncluded: serverAnalysis.serverMetadata.taxIncluded,
                serviceFeeIncluded: serverAnalysis.serverMetadata.serviceFeeIncluded,
                estimatedAttendees: (serverAnalysis.serverMetadata as any)?.estimatedAttendees,
                taxRate: (serverAnalysis.serverMetadata as any)?.taxRate,
                serviceFeeRate: (serverAnalysis.serverMetadata as any)?.serviceFeeRate
              } as any);
            }
            if (!extraction) {
              try {
                const ex = await getExtraction(sessionId);
                setExtraction(ex);
              } catch {}
            }
          } catch {
            setStep('analysis');
            return;
          } finally {
            analysisFetchInFlight.current = false;
          }
        }
        setStep('complete');
        setError(null);
      } else if (status.status === 'AnalysisInProgress') {
        setStep('analysis');
        setError(null);
      } else if (status.status === 'FailedAnalysis') {
        setError(status.lastError ?? 'We hit a snag running the analysis. Please review your details and try again.');
        setStep('meta-form');
      } else if (status.status === 'ExtractionComplete') {
        if (!extraction) {
          try {
            const ex = await getExtraction(sessionId);
            setExtraction(ex);
            mergeMetadata({
              eventName: (ex as any).menu.eventName ?? undefined,
              eventType: ex.menu.eventType ?? undefined,
              eventLocation: ex.menu.eventLocation ?? undefined,
              eventDate: ex.menu.eventDate ?? undefined,
              taxIncluded: ex.menu.taxIncluded ?? undefined,
              serviceFeeIncluded: ex.menu.serviceFeeIncluded ?? undefined,
              taxRate: (ex.menu as any)?.taxRate ?? undefined,
              serviceFeeRate: (ex.menu as any)?.serviceFeeRate ?? undefined
            } as any);
          } catch {
            setStep('extraction');
            return;
          }
        }
        if (step === 'extraction' || step === 'upload') {
          setStep('review');
        }
        setError(null);
      } else if (status.status === 'ExtractionInProgress') {
        setStep('extraction');
        setError(null);
      } else if (status.status === 'FailedExtraction') {
        setError(
          status.lastError ?? 'The AI was unable to read your menu. Please double-check the file and try again.'
        );
        setStep('upload');
      } else if (status.status === 'UploadPending' || status.status === 'Created') {
        // Avoid downgrading if user has already progressed past upload
        if (step === 'upload' || step === 'email') {
          setStep('upload');
        }
      }
    } catch (e) {
      try {
        if (storage) storage.removeItem('labs.menuAnalysis.state');
        setSessionId(null);
        setEmail(null);
        setExtraction(null);
        setAnalysis(null);
        setMetadata({});
        setStep('upload');
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', '/');
        }
      } catch {}
    } finally {
      if (isBootHydration.current) {
        setIsHydrating(false);
        isBootHydration.current = false;
      }
    }
  }, [analysis, extraction, mergeMetadata, sessionId, step, storage]);

  useEffect(() => {
    if (!sessionId) return;
    if (isBootHydration.current) setIsHydrating(true);
    void refreshStatus();
  }, [refreshStatus, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (step !== 'extraction' && step !== 'analysis') return;

    // Clear existing timers
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    pollTimeoutRef.current = setTimeout(() => {
      void refreshStatus();
      pollIntervalRef.current = setInterval(() => {
        void refreshStatus();
      }, 10000);
    }, 10000);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [refreshStatus, sessionId, step]);

  const identifyMutation = useMutation({
    mutationFn: async (payload: CreateSessionRequest) => {
      if (!sessionId) {
        throw new Error('Session not created');
      }
      const result = await identifySession(sessionId, payload);
      setEmail(payload.email);
      return result;
    },
    onSuccess: () => {
      setError(null);
    },
    onError: () => {
      setError('We were unable to save your email. Please try again.');
    }
  });

  type RequestUploadArgs = { sessionId: string; payload: UploadUrlRequest };

  const requestUploadMutation = useMutation({
    mutationFn: ({ sessionId: sid, payload }: RequestUploadArgs) => {
      if (payload.fileSizeBytes > MAX_FILE_BYTES) {
        throw new Error('File too large');
      }
      if (!sid) {
        throw new Error('Session not created');
      }
      return requestUploadUrl(sid, payload);
    },
    onSuccess: (data) => {
      setUploadInfo(data);
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message === 'File too large') {
        setError('Files must be 10MB or smaller.');
        return;
      }
      setError('We were unable to prepare your upload. Please try again.');
    }
  });

  const extractionMutation = useMutation({
    mutationFn: async () => {
      if (!sessionId) {
        throw new Error('Session not created');
      }
      setStep('extraction');
      const status = await triggerExtraction(sessionId);
      setError(null);
      return status;
    },
    onSuccess: () => {
      void refreshStatus();
    },
    onError: () => {
      setError('The AI was unable to read your menu. Please double-check the file and try again.');
      setStep('upload');
    }
  });

  const analysisMutation = useMutation<SessionStatusResponse, unknown, PhaseTwoMetadataRequest>({
    mutationFn: async (payload: PhaseTwoMetadataRequest) => {
      if (!sessionId) {
        throw new Error('Session not created');
      }
      setStep('analysis');
      return submitMetadata(sessionId, payload);
    },
    onSuccess: (_result, payload) => {
      mergeMetadata(payload);
      setError(null);
      setStep('analysis');
      void refreshStatus();
    },
    onError: () => {
      setError('We hit a snag running the analysis. Please review your details and try again.');
      setStep('meta-form');
    }
  });

  const saveMetadataAndProceedToEmail = async (payload: PhaseTwoMetadataRequest) => {
    mergeMetadata(payload);
    setStep('email');
  };

  const proceedFromReview = async () => {
    if (!extraction) {
      // Fallback: if somehow review has no extraction, retry extraction
      try { await extractionMutation.mutateAsync(); } catch {}
      return;
    }
    // Begin typeform-like flow
    setStep('meta-form');
  };

  const uploadFile = async (file: File) => {
    setIsFileUploading(true); // flip immediately so UI reflects progress while we create session and fetch SAS
    try {
      let sid = sessionId;
      if (!sid) {
        const created = await createAnonymousSession();
        sid = created.sessionId;
        setSessionId(created.sessionId);
        // Navigate to deep-linkable URL
        try {
          if (typeof window !== 'undefined') {
            const path = `/menuanalysis/${created.sessionId}`;
            window.history.replaceState({}, '', path);
          }
        } catch {}
      }

      const upload = await requestUploadMutation.mutateAsync({
        sessionId: sid!,
        payload: {
          fileName: file.name,
          contentType: file.type,
          fileSizeBytes: file.size
        }
      });

      const url = buildBlobUploadUrl(upload);

      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type
        }
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      setStep('extraction');
      return upload;
    } finally {
      setIsFileUploading(false);
    }
  };

  const reset = () => {
    setSessionId(null);
    setEmail(null);
    setUploadInfo(null);
    setExtraction(null);
    setAnalysis(null);
    setMetadata({});
    setIsFileUploading(false);
    setError(null);
    setStep('upload');
    try {
      if (storage) storage.removeItem('labs.menuAnalysis.state');
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', '/');
      }
    } catch {}
  };

  return {
    sessionId,
    uploadInfo,
    extraction,
    analysis,
    metadata,
    step,
    email,
    error,
    setError,
    hasFeedback,
    errorRetry: async () => {
      // Clear the error banner immediately so the UI reflects the retry
      setError(null);
      // Retry identify if we're on the email step and have payload
      if (step === 'email' && lastIdentifyPayload) {
        try {
          await identifyMutation.mutateAsync(lastIdentifyPayload);
        } catch {}
        return;
      }
      // Retry analysis if we have the last payload
      if (lastAnalysisPayload) {
        setStep('analysis');
        try {
          await analysisMutation.mutateAsync(lastAnalysisPayload);
        } catch {}
        return;
      }
      // Otherwise, retry extraction
      try {
        await extractionMutation.mutateAsync();
      } catch {}
    },
    isHydrating,
    actions: {
      submitFeedback: async (rating: 'up' | 'down', comment?: string) => {
        if (!sessionId) return;
        await submitFeedback(sessionId, { rating, comment });
        setHasFeedback(true);
      },
      identify: async (values: CreateSessionRequest) => {
        setLastIdentifyPayload(values);
        await identifyMutation.mutateAsync(values);
        const payload: PhaseTwoMetadataRequest = {
          eventName: (metadata as any)?.eventName ?? (extraction as any)?.menu?.eventName ?? undefined,
          eventType: metadata?.eventType ?? extraction?.menu.eventType ?? undefined,
          eventLocation: metadata?.eventLocation ?? extraction?.menu.eventLocation ?? undefined,
          eventDate: metadata?.eventDate ?? extraction?.menu.eventDate ?? undefined,
          taxIncluded: metadata?.taxIncluded ?? extraction?.menu.taxIncluded ?? undefined,
          serviceFeeIncluded: metadata?.serviceFeeIncluded ?? extraction?.menu.serviceFeeIncluded ?? undefined,
          estimatedAttendees: (metadata as any)?.estimatedAttendees,
          taxRate: (metadata as any)?.taxRate,
          serviceFeeRate: (metadata as any)?.serviceFeeRate
        };
        setLastAnalysisPayload(payload);
        await analysisMutation.mutateAsync(payload);
      },
      uploadFile,
      runExtraction: extractionMutation.mutateAsync,
      runAnalysis: async (payload: PhaseTwoMetadataRequest) => {
        setLastAnalysisPayload(payload);
        return analysisMutation.mutateAsync(payload);
      },
      proceedFromMetadata: saveMetadataAndProceedToEmail,
      proceedFromReview,
      saveBasicsAndNext: async (values: { eventName: string; eventType?: string; eventDate: string }) => {
        mergeMetadata(values as any);
        setStep('meta-when-where');
      },
      saveWhenWhereAndNext: async (values: { eventLocation: string }) => {
        mergeMetadata(values as any);
        setStep('meta-tax');
      },
      saveTaxAndNext: async (values: { included: 'Included' | 'Additional' | 'NotApplicable'; rate?: number }) => {
        const updates: any = { taxIncluded: values.included };
        if (values.included === 'Included' || values.included === 'Additional') {
          if (typeof values.rate === 'number') updates.taxRate = values.rate;
        } else {
          updates.taxRate = undefined;
        }
        mergeMetadata(updates);
        setStep('meta-service');
      },
      saveServiceAndNext: async (values: { included: 'Included' | 'Additional' | 'NotApplicable'; rate?: number }) => {
        const updates: any = { serviceFeeIncluded: values.included };
        if (values.included === 'Included' || values.included === 'Additional') {
          if (typeof values.rate === 'number') updates.serviceFeeRate = values.rate;
        } else {
          updates.serviceFeeRate = undefined;
        }
        mergeMetadata(updates);
        setStep('email');
      },
      backToReview: () => setStep('review'),
      backToBasics: () => setStep('meta-basics'),
      backToWhenWhere: () => setStep('meta-when-where'),
      backToTax: () => setStep('meta-tax'),
      applyMetadata: (updates: Partial<PhaseTwoMetadataRequest>) => {
        mergeMetadata(updates);
      },
      gotoEmailAfterMetadata: () => setStep('email'),
      reset
    },
    isLoading:
      identifyMutation.isPending ||
      requestUploadMutation.isPending ||
      extractionMutation.isPending ||
      analysisMutation.isPending ||
      isFileUploading
  };
};
